import d3 from 'd3';
import { Utils } from 'mbc/dist/mbc-core';
import 'mbc/dist/mbc-vertical-bar.css';
import MBCBarChart from 'mbc/dist/mbc-vertical-bar';

const HypoReturnBarChart = MBCBarChart.extend({
  y1SymbolAlign: 'right',
  y2SymbolAlign: 'left',
  barGroupFixedPadding: null,
  barPadding: 3,
  barOuterPadding: 0,
  yPaddingSmall: 12,
  yPaddingBig: 12,
  minBarWidth: 15,
  minGroupBarWidth: null,
  /**
   * Configure all axes used in the chart.
   */
  buildAxes() {
    const _this = this;
    const scales = this.get('scales');
    const axes = this.get('axes');
    const calculatedTicks = Utils.chart.getTickValues(
      this.get('_yDomain'),
      this.get('ticksNumber')
    );
    const tickValues = this.get('tickValues') || calculatedTicks;
    let tickValues2;
    let y2axis;

    const tickFormat = function(symbol, symbolAlign, length, formatFn) {
      return function(d, i) {
        let value = _this.tickFormat(formatFn)(d, i);
        if (value[0] === '-') {
          const sliced = value.slice(1);
          value = `\u2212${sliced}`;
        }
        if (symbol && i === length - 1) {
          return symbolAlign === 'left' ? symbol + value : value + symbol;
        }
        return value;
      };
    };

    let xAxis = d3.svg
      .axis()
      .scale(scales.scale('x'))
      .orient(this.get('xAxisOrient'))
      .tickFormat(function(d) {
        return Utils.string.unmask(d);
      });

    if (this.get('xAxisDateLabel')) {
      xAxis = this.get('dateAxis').getAxis();
    }

    const yAxis = d3.svg
      .axis()
      .scale(scales.scale('y1'))
      .orient(this.get('y1AxisOrient'))
      .tickSize(this.get('yTickSize'))
      .tickValues(tickValues)
      .tickFormat(
        tickFormat(
          this.get('y1TickSymbol'),
          this.get('y1SymbolAlign'),
          tickValues.length,
          this.get('y1TickFormat')
        )
      );

    yAxis.axisLabel = this.get('y1AxisLabel');
    axes.axis('x', xAxis);
    axes.axis('y1', yAxis);

    if (this.get('y2')) {
      tickValues2 = Utils.chart.getTickValues(this.get('y2Domain'));

      y2axis = d3.svg
        .axis()
        .scale(scales.scale('y2'))
        .orient(this.get('y2AxisOrient'))
        .tickSize(this.get('yTickSize'))
        .tickValues(tickValues2)
        .tickFormat(
          tickFormat(
            this.get('y2TickSymbol'),
            this.get('y1SymbolAlign'),
            tickValues2.length,
            this.get('y2TickFormat')
          )
        );

      axes.axis('y2', y2axis);
    }
  },
  buildScales() {
    this.__super__.buildScales.call(this);
    if (this.hasBarData()) {
      this.reBuildScale();
    }
  },
  getMargins() {
    const margin = this.__super__.getMargins.call(this);
    margin.bottom = 0;
    return margin;
  },
  reBuildScale() {
    const xScroll = this.get('xScroll');
    const scales = this.get('scales');
    const x0Scale = scales.scale('x0');
    const xScale = scales.scale('x');
    const barGroupPadding = this.get('barGroupPadding');
    const barPadding = this.get('barPadding');
    const barOuterPadding = this.get('barOuterPadding');
    const xDomain = this.get('xDomain');
    const x0Domain = this.get('x0Domain');
    let chartWidth;
    let groupBarWidth;
    let barGroupStep;
    let barStep;

    if (xScroll) {
      chartWidth = this.get('minChartWidth');
      groupBarWidth = this.get('minGroupBarWidth');
      barGroupStep = chartWidth / xDomain.length;
      barStep = groupBarWidth / x0Domain.length;
    } else {
      const canvas = this.get('canvas');
      chartWidth = canvas.width();
      barGroupStep = canvas.width() / xDomain.length;
      groupBarWidth = barGroupStep - barGroupPadding;
      barStep = groupBarWidth / x0Domain.length;
    }
    xScale.rangeBands([0, chartWidth], barGroupPadding / barGroupStep, 0);
    x0Scale.rangeBands([0, groupBarWidth], barPadding / barStep, barOuterPadding / barStep);
  },
  hasBarData() {
    const data = this.get('data').get(this.get('y1DataSource'));
    return data.length > 0 && data[0].values.length > 0;
  },
  getBandWidth(domain, range) {
    const scale = d3.scale.ordinal(domain).rangeBands(range);
    return scale.rangeBand();
  },
  setBarGroupPadding() {
    const barGroupFixedPadding = this.get('barGroupFixedPadding');
    if (barGroupFixedPadding !== null) {
      this.set('barGroupPadding', barGroupFixedPadding);
    } else {
      this.__super__.setBarGroupPadding.call(this);
    }
  },
  beforeDraw() {
    const rawFlag = this.__super__.beforeDraw.call(this);
    if (this.hasBarData()) {
      this.setMinChartWidth();
      this.updateCanvas();
      return false;
    }
    return rawFlag;
  },
  matchMediaBreakPoint(breakPoint) {
    return window.matchMedia(`(min-width: ${breakPoint}px)`).matches;
  },
  setMinChartWidth() {
    if (this.hasBarData()) {
      const data = this.get('data').get(this.get('y1DataSource'));
      let minGroupBarWidth = this.get('minGroupBarWidth');

      if (!minGroupBarWidth) {
        const barNumbersPerGroup = data[0].values.length;
        const minBarWidth = this.get('minBarWidth');
        const barPadding = this.get('barPadding');
        const barOuterPadding = this.get('barOuterPadding');
        minGroupBarWidth =
          minBarWidth * barNumbersPerGroup +
          (barNumbersPerGroup - 1) * barPadding +
          barOuterPadding * 2;
        this.set('minGroupBarWidth', minGroupBarWidth);
      }

      const barGroupPadding = this.get('barGroupPadding');
      const barGroupOuterPadding = 0;
      const barGroupNumbers = data.length;
      const minChartWidth =
        minGroupBarWidth * barGroupNumbers +
        (barGroupNumbers - 1) * barGroupPadding +
        barGroupOuterPadding * 2;
      this.set('minChartWidth', minChartWidth);

      const canvas = this.get('canvas');
      const margin = canvas.get('margin');
      const container = canvas.get('container');
      const availableWidth = container.offsetWidth - margin.left - margin.right;
      if (minChartWidth > availableWidth) {
        this.set('xScroll', true);
        canvas.set('width', minChartWidth);
      } else {
        this.set('xScroll', false);
        canvas.set('width', availableWidth);
      }
    }
  },
  calculateCanvasHeight() {
    const canvas = this.get('canvas');
    const selection = canvas.get('selection')[0][0];
    const container = canvas.get('container');
    const margin = canvas.get('margin');

    canvas.set('height', container.offsetHeight - margin.top - margin.bottom);
    canvas.set('outerHeight', selection.offsetHeight);
  },
  updateCanvas() {
    this.calculateCanvasHeight();
    const canvas = this.get('canvas');
    const margin = canvas.get('margin');
    canvas.buildSVG();
    this.set('svgWidth', canvas.get('width') + margin.left + margin.right);
  },
});

export default HypoReturnBarChart;
